import * as THREE from 'three';

// Manages 3D equipment slots and item visualization
export class EquipmentManager {
    constructor(gameState, itemsData, car) {
        this.gameState = gameState;
        this.itemsData = itemsData;
        this.car = car;

        // Equipment slots (invisible containers attached to car)
        this.equipmentSlots = {
            dashboard: null,
            keychain: null,
            rearview: null
        };

        // Currently equipped item meshes
        this.equippedMeshes = {
            dashboard: null,
            keychain: null,
            rearview: null
        };

        // Configuration - can be set before calling createSlots()
        this.slotPositions = {
            dashboard: { x: 0.1, y: 1, z: 0.7 },
            keychain: { x: 0.3, y: 1, z: 0.7 },
            rearview: { x: -0.2, y: 1.5, z: 0.7 }
        };

        this.itemScale = 1.0; // Global scale multiplier for all items
    }

    // Create equipment slots attached to the car
    createSlots() {
        if (!this.car) {
            console.warn('EquipmentManager: Cannot create slots without car');
            return;
        }

        // Dashboard slot
        const dashSlot = new THREE.Object3D();
        const dashPos = this.slotPositions.dashboard;
        dashSlot.position.set(dashPos.x, dashPos.y, dashPos.z);
        dashSlot.userData.slotType = 'dashboard';
        this.car.add(dashSlot);
        this.equipmentSlots.dashboard = dashSlot;

        // Keychain slot
        const keySlot = new THREE.Object3D();
        const keyPos = this.slotPositions.keychain;
        keySlot.position.set(keyPos.x, keyPos.y, keyPos.z);
        keySlot.userData.slotType = 'keychain';
        this.car.add(keySlot);
        this.equipmentSlots.keychain = keySlot;

        // Rearview slot
        const rearviewSlot = new THREE.Object3D();
        const rearviewPos = this.slotPositions.rearview;
        rearviewSlot.position.set(rearviewPos.x, rearviewPos.y, rearviewPos.z);
        rearviewSlot.userData.slotType = 'rearview';
        this.car.add(rearviewSlot);
        this.equipmentSlots.rearview = rearviewSlot;

        // Display currently equipped items
        this.updateEquippedItems();
    }

    // Update all equipment slots based on gameState
    updateEquippedItems() {
        if (!this.car || !this.itemsData) {
            console.log('⏳ updateEquippedItems: waiting for', !this.car ? 'car' : 'itemsData');
            return;
        }

        const equippedSlots = this.gameState.getEquippedItems();
        console.log('🔄 Updating equipped items:', Object.keys(equippedSlots).map(slot =>
            equippedSlots[slot] ? `${slot}: ${equippedSlots[slot].name}` : `${slot}: empty`
        ).join(', '));

        // Update each slot (equippedSlots now contains full item objects)
        Object.keys(equippedSlots).forEach(slotType => {
            const item = equippedSlots[slotType];
            const slot = this.equipmentSlots[slotType];

            if (!slot) {
                return;
            }

            // Remove existing mesh if any
            if (this.equippedMeshes[slotType]) {
                console.log(`🗑️ Removing mesh from ${slotType}`);
                slot.remove(this.equippedMeshes[slotType]);
                this.equippedMeshes[slotType] = null;
            }

            // Add new mesh if item is equipped
            if (item) {
                console.log(`✨ Equipping ${item.name} in ${slotType} - Color: ${this.itemsData.rarityColors[item.rarity]}`);
                const mesh = this.createItemPrimitive(item);
                slot.add(mesh);
                this.equippedMeshes[slotType] = mesh;
                console.log(`✅ Mesh added to slot. Slot position:`, slot.position);
            }
        });
    }

    // Create a 3D primitive for an item
    // Primitive shape is determined by slot type:
    //   dashboard -> cube
    //   keychain -> sphere
    //   rearview -> triangle (pyramid/cone)
    createItemPrimitive(item) {
        const rarityColor = this.itemsData.rarityColors[item.rarity];
        let geometry;

        // Determine primitive from slot type (not item.primitive which may not exist)
        const slot = item.slot || item.slotType;
        switch (slot) {
            case 'dashboard':
                geometry = new THREE.BoxGeometry(1, 1, 1);
                break;
            case 'keychain':
                geometry = new THREE.SphereGeometry(0.6, 16, 16);
                break;
            case 'rearview':
                geometry = new THREE.ConeGeometry(0.5, 1.2, 3);
                break;
            default:
                geometry = new THREE.BoxGeometry(1, 1, 1);
        }

        const material = new THREE.MeshBasicMaterial({
            color: rarityColor,
            transparent: false,
            opacity: 1.0,
            side: THREE.DoubleSide
        });

        const mesh = new THREE.Mesh(geometry, material);

        // Add slow rotation animation
        mesh.userData.rotationSpeed = 1.5;

        // Apply scale multiplier
        mesh.scale.set(this.itemScale, this.itemScale, this.itemScale);

        return mesh;
    }

    // Update equipped item animations (call in game loop)
    update(deltaTime) {
        // Rotate equipped item meshes
        Object.values(this.equippedMeshes).forEach(mesh => {
            if (mesh && mesh.userData.rotationSpeed) {
                mesh.rotation.y += mesh.userData.rotationSpeed * deltaTime;
            }
        });
    }

    // Set item data (called after items.json is loaded)
    setItemsData(itemsData) {
        this.itemsData = itemsData;
        // Auto-update equipped items if slots are already created
        // This handles the case where car loaded before items data
        if (this.equipmentSlots.dashboard) {
            this.updateEquippedItems();
        }
    }

    // Dispose of all equipped meshes
    dispose() {
        // Remove and dispose all equipped meshes
        Object.keys(this.equippedMeshes).forEach(slot => {
            const mesh = this.equippedMeshes[slot];
            if (mesh) {
                if (mesh.parent) mesh.parent.remove(mesh);
                if (mesh.geometry) mesh.geometry.dispose();
                if (mesh.material) mesh.material.dispose();
            }
        });
        this.equippedMeshes = {};
    }
}
